<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isOpen" class="wizard-backdrop" @click.self="closeModal">
        <div class="wizard-panel">

          <!-- Header -->
          <div class="wizard-header">
            <div>
              <p class="wizard-step-label">Add from profile</p>
              <h2 class="wizard-title">{{ sectionLabels[dataType] }}</h2>
            </div>
            <button @click="closeModal" class="wizard-close">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Loading -->
          <div v-if="isLoading" class="wizard-loading">
            <div class="w-5 h-5 border-2 border-zinc-100 border-t-primary rounded-full animate-spin"></div>
            <p class="text-sm text-zinc-400">Loading your profile…</p>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="wizard-error">
            <p class="text-sm text-zinc-500">{{ error }}</p>
            <button @click="fetchMasterData" class="wiz-btn-primary text-xs mt-3">Try Again</button>
          </div>

          <!-- Selection -->
          <div v-else class="wizard-body wizard-body--scroll">

            <p v-if="!currentSectionData.length" class="text-sm text-zinc-400 text-center py-6">
              No {{ sectionLabels[dataType].toLowerCase() }} in your profile yet.
              <router-link to="/profile" class="text-primary hover:opacity-70">Add entries first →</router-link>
            </p>

            <!-- Skills: multi-select cards -->
            <div v-else-if="dataType === 'skills'" class="flex flex-wrap gap-2">
              <div
                v-for="skill in currentSectionData"
                :key="skill.id"
                @click="toggleSkill(skill.id)"
                class="entry-card"
                style="flex: 0 1 calc(50% - 0.25rem)"
                :class="{ 'entry-card--selected': isSelected(skill.id) }"
              >
                <div v-if="isSelected(skill.id)" class="entry-card-check">
                  <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                </div>
                <p class="text-sm font-semibold text-zinc-900 dark:text-white truncate">{{ skill.category_name }}</p>
                <p class="text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2 mt-0.5">{{ skill.content }}</p>
              </div>
            </div>

            <!-- Entries: one family per row, canonical + variants side by side -->
            <div v-else class="flex flex-col gap-3">
              <div v-for="entry in currentSectionData" :key="entry.id" class="flex gap-2 items-stretch">
                <div
                  v-for="version in familyVersions(entry)"
                  :key="version.id"
                  @click="toggleVersion(entry, version.id)"
                  class="entry-card flex-1 min-w-0"
                  :class="{ 'entry-card--selected': isSelected(version.id) }"
                >
                  <div v-if="isSelected(version.id)" class="entry-card-check">
                    <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                  </div>
                  <span v-if="version.variant_label" class="entry-variant-pill">{{ version.variant_label }}</span>
                  <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate" :class="{ 'mt-1': version.variant_label }">
                    {{ entryTitle(version) }}
                  </p>
                  <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ entrySubtitle(version) }}</p>
                  <p v-if="version.start_year" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">
                    {{ version.start_year }}–{{ version.is_current ? 'Present' : version.end_year }}
                  </p>
                </div>
              </div>
            </div>

          </div>

          <!-- Footer -->
          <div v-if="!isLoading && !error" class="wizard-footer">
            <button @click="closeModal" class="wiz-btn-secondary">Cancel</button>
            <button @click="addSelectedItems" :disabled="selectedIds.length === 0" class="wiz-btn-primary">
              {{ addButtonLabel }}
            </button>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, watch } from 'vue'

export default {
  name: 'MasterDataModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false
    },
    dataType: {
      type: String, // 'education', 'experience', 'activities', 'skills'
      required: true,
      validator: (value) => ['education', 'experience', 'activities', 'skills'].includes(value)
    }
  },
  emits: ['close', 'itemsSelected'],
  setup(props, { emit }) {
    const isLoading = ref(true)
    const error = ref(null)
    const masterData = ref({ education: [], experience: [], activities: [], skills: [] })
    const selectedIds = ref([])

    const sectionLabels = {
      education: 'Education',
      experience: 'Experience',
      activities: 'Activities & Leadership',
      skills: 'Skills & Interests'
    }

    const currentSectionData = computed(() => masterData.value[props.dataType] || [])

    const addButtonLabel = computed(() => {
      const count = selectedIds.value.length
      if (count === 0) return 'Add entries'
      return `Add ${count} ${count === 1 ? 'entry' : 'entries'}`
    })

    const getCSRFToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    const fetchMasterData = async () => {
      try {
        isLoading.value = true
        error.value = null
        const res = await fetch('/api/master_data/all', {
          headers: { Accept: 'application/json', 'X-CSRF-Token': getCSRFToken() }
        })
        const data = await res.json()
        if (data.success) {
          masterData.value = data.data
        } else {
          error.value = data.message || 'Failed to load profile data'
        }
      } catch {
        error.value = 'Failed to connect to server.'
      } finally {
        isLoading.value = false
      }
    }

    const familyVersions = (entry) => [entry, ...(entry.variants || [])]
    const familyIds = (entry) => familyVersions(entry).map(v => v.id)

    const isSelected = (id) => selectedIds.value.includes(id)

    // Click a card: if already selected → deselect the whole family;
    // if not → select this version, deselect siblings in the family
    const toggleVersion = (entry, versionId) => {
      const fids = familyIds(entry)
      if (selectedIds.value.includes(versionId)) {
        selectedIds.value = selectedIds.value.filter(id => !fids.includes(id))
      } else {
        selectedIds.value = [...selectedIds.value.filter(id => !fids.includes(id)), versionId]
      }
    }

    const toggleSkill = (id) => {
      selectedIds.value = selectedIds.value.includes(id)
        ? selectedIds.value.filter(i => i !== id)
        : [...selectedIds.value, id]
    }

    const entryTitle = (version) => {
      if (props.dataType === 'education') return version.program_name || version.institution
      if (props.dataType === 'experience') return version.job_title
      return version.role || version.organization
    }

    const entrySubtitle = (version) => {
      if (props.dataType === 'education') return version.institution
      if (props.dataType === 'experience') return version.company
      return version.organization
    }

    const addSelectedItems = () => {
      // Resolve the chosen version (canonical or variant) per family
      const items = []
      currentSectionData.value.forEach(entry => {
        const chosen = familyVersions(entry).find(v => selectedIds.value.includes(v.id))
        if (chosen) items.push(chosen)
      })
      if (!items.length) return

      emit('itemsSelected', { type: props.dataType, items })
      closeModal()
    }

    const closeModal = () => {
      selectedIds.value = []
      emit('close')
    }

    watch(() => props.dataType, () => { selectedIds.value = [] })

    watch(() => props.isOpen, (open) => {
      if (open) {
        selectedIds.value = []
        fetchMasterData()
      }
    })

    return {
      isLoading,
      error,
      selectedIds,
      sectionLabels,
      currentSectionData,
      addButtonLabel,
      fetchMasterData,
      familyVersions,
      isSelected,
      toggleVersion,
      toggleSkill,
      entryTitle,
      entrySubtitle,
      addSelectedItems,
      closeModal
    }
  }
}
</script>

<style scoped>
/* Shared wizard styling — mirrors CvCreationWizard.vue */
.wizard-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center p-4;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
}
.wizard-panel {
  @apply bg-white dark:bg-gray-950 border border-zinc-100 dark:border-zinc-800 rounded-2xl w-full max-w-2xl flex flex-col overflow-hidden;
  max-height: 90vh;
}
.wizard-header {
  @apply flex items-start justify-between px-6 pt-6 pb-5 border-b border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.wizard-step-label {
  @apply text-xs font-semibold uppercase tracking-widest text-gold dark:text-gold-400 mb-1;
}
.wizard-title {
  @apply text-lg font-semibold text-zinc-900 dark:text-white;
}
.wizard-close {
  @apply p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors;
}
.wizard-loading {
  @apply flex flex-col items-center justify-center gap-3 py-12;
}
.wizard-error {
  @apply flex flex-col items-center justify-center py-10;
}
.wizard-body {
  @apply px-6 py-5 flex-1;
}
.wizard-body--scroll {
  @apply overflow-y-auto;
}
.wizard-footer {
  @apply flex items-center justify-between px-6 py-4 border-t border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.wiz-btn-primary {
  @apply inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity;
}
.wiz-btn-secondary {
  @apply inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors;
}
.entry-card {
  @apply relative border border-zinc-100 dark:border-zinc-800 rounded-xl p-3 cursor-pointer transition-colors hover:border-zinc-300 dark:hover:border-zinc-600;
}
.entry-card--selected {
  @apply border-gold dark:border-gold-500 bg-gold-50/40 dark:bg-gold-900/10;
}
.entry-card-check {
  @apply absolute top-2 right-2 w-4 h-4 rounded-full bg-gold flex items-center justify-center flex-shrink-0;
}
.entry-variant-pill {
  @apply inline-flex items-center border border-zinc-200 dark:border-zinc-700 rounded-full text-[10px] font-medium text-zinc-500 dark:text-zinc-400 px-2 py-0.5 leading-tight;
}
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(4px);
}
</style>
